import { useTranslation } from "react-i18next";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ArrowLeft, Check, ChevronRight, Circle, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Checklist, ChecklistStep } from "./checklist";

const ORDER = { todo: 0, done: 1, locked: 2 } as const;

const stepLabelKey = (s: ChecklistStep) =>
  `gettingStarted.steps.${s.viaEmail ? "alertsEmail" : s.id}`;

/**
 * The one line the account menu keeps for the checklist: progress and the
 * next step, opening the full list in place. Touch screens can't hover the
 * avatar, and keyboard users tab into the menu, so this is their way in.
 */
export function GettingStartedRow({
  checklist,
  onOpen,
}: {
  checklist: Checklist;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const next = checklist.steps.find((s) => s.status === "todo");
  return (
    <DropdownMenu.Item
      data-testid="getting-started-row"
      // Stay open: the menu swaps to the steps.
      onSelect={(e) => {
        e.preventDefault();
        onOpen();
      }}
      className="mb-1 flex min-h-11 cursor-pointer items-center gap-2 rounded-xl bg-surface-tertiary/60 px-2.5 py-1.5 outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          <span className="text-sm font-semibold text-text-primary">{t("gettingStarted.title")}</span>
          <span className="text-xs font-medium tabular-nums text-text-muted">
            {checklist.done}/{checklist.total}
          </span>
        </span>
        <span className="block truncate text-xs text-text-secondary">
          {next
            ? t("gettingStarted.next", { step: t(stepLabelKey(next)) })
            : t("gettingStarted.allSet")}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
    </DropdownMenu.Item>
  );
}

/**
 * The checklist as its own view of the account menu (Spotify's "Getting
 * started" panel): what's left to be ready for gigs, missing first, each step
 * one tap from where it gets done. `onBack` adds a way back to the account
 * view when the member came here from the row.
 */
export function GettingStartedSection({
  checklist,
  onNavigate,
  onBack,
}: {
  checklist: Checklist;
  onNavigate: (href: string) => void;
  onBack?: () => void;
}) {
  const { t } = useTranslation();
  const steps = [...checklist.steps].sort(
    (a, b) => ORDER[a.status] - ORDER[b.status],
  );

  return (
    <div data-testid="getting-started-section">
      {onBack && (
        <DropdownMenu.Item
          data-testid="getting-started-back"
          onSelect={(e) => {
            e.preventDefault();
            onBack();
          }}
          className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-text-secondary outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary"
        >
          <ArrowLeft className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
          {t("gettingStarted.back")}
        </DropdownMenu.Item>
      )}
      <div className="flex items-start justify-between gap-2 px-2 pt-1.5">
        <p className="font-semibold text-text-primary">{t("gettingStarted.title")}</p>
        <p className="shrink-0 pt-0.5 text-sm font-medium tabular-nums text-text-muted">
          {checklist.done}/{checklist.total}
        </p>
      </div>
      {checklist.complete ? (
        <p className="px-2 pb-2 pt-1 text-sm text-text-secondary">
          {t("gettingStarted.allSet")}
        </p>
      ) : (
        <>
          <p className="px-2 pb-1 text-xs text-text-tertiary">
            {t("gettingStarted.subtitle")}
          </p>
          <div role="group" aria-label={t("gettingStarted.title")}>
            {steps.map((s) => (
              <StepRow key={s.id} step={s} onNavigate={onNavigate} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StepRow({
  step,
  onNavigate,
}: {
  step: ChecklistStep;
  onNavigate: (href: string) => void;
}) {
  const { t } = useTranslation();
  const Icon =
    step.status === "done" ? Check : step.status === "locked" ? Lock : Circle;
  const content = (
    <>
      <Icon
        className={cn("h-4 w-4 shrink-0", step.status === "done" ? "text-brand-500" : "text-text-muted")}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className={step.status === "done" ? "text-text-muted line-through" : "text-text-primary"}>
          {t(stepLabelKey(step))}
        </span>
        {step.status === "locked" && (
          <span className="block text-[11px] text-text-muted">
            {t("gettingStarted.locked")}
          </span>
        )}
      </span>
    </>
  );
  const rowClass = "flex min-h-10 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm";
  return step.status === "todo" ? (
    <DropdownMenu.Item
      data-testid="getting-started-step"
      data-step={step.id}
      className={cn(rowClass, "cursor-pointer outline-none hover:bg-surface-tertiary focus:bg-surface-tertiary")}
      onSelect={() => onNavigate(step.href)}
    >
      {content}
    </DropdownMenu.Item>
  ) : (
    <div data-testid="getting-started-step" data-step={step.id} className={rowClass}>
      {content}
    </div>
  );
}
